import React, { Component } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

import widgetRegistry from "./config/widgetRegistry";

import WidgetKPICards from "./widgets/WidgetKPICards";
import WidgetSatisfaction from "./widgets/WidgetSatisfaction";
import WidgetRendement from "./widgets/WidgetRendement";
import WidgetCA from "./widgets/WidgetCA";
import WidgetMissions from "./widgets/WidgetMissions";
import WidgetPodiums from "./widgets/WidgetPodiums";
import WidgetAlerts from "./widgets/WidgetAlerts";
import WidgetTeamOverview from "./widgets/WidgetTeamOverview";
import WidgetPersonalPerf from "./widgets/WidgetPersonalPerf";
import WidgetQuickActions from "./widgets/WidgetQuickActions";
import WidgetInterviewProgress from "./widgets/WidgetInterviewProgress";
import WidgetRepairRates from "./widgets/WidgetRepairRates";

const widgetComponents = {
  "kpi-cards": WidgetKPICards,
  satisfaction: WidgetSatisfaction,
  rendement: WidgetRendement,
  ca: WidgetCA,
  missions: WidgetMissions,
  podiums: WidgetPodiums,
  alerts: WidgetAlerts,
  "team-overview": WidgetTeamOverview,
  "personal-perf": WidgetPersonalPerf,
  "quick-actions": WidgetQuickActions,
  "interview-progress": WidgetInterviewProgress,
  "repair-rates": WidgetRepairRates,
};

const getWidthPercent = (size) => {
  if (!size || !size.md) return "100%";
  // md column count out of 12
  return `${(size.md / 12) * 100}%`;
};

export default class DashboardGrid extends Component {
  handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;

    const { layout, onReorder } = this.props;
    const visibleWidgets = layout
      .filter((w) => w.visible)
      .sort((a, b) => a.position - b.position);
    const reordered = Array.from(visibleWidgets);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    const hiddenWidgets = layout.filter((w) => !w.visible);
    const newLayout = [
      ...reordered.map((w, idx) => ({ ...w, position: idx })),
      ...hiddenWidgets,
    ];

    onReorder(newLayout);
  };

  render() {
    const { layout, isCustomizing } = this.props;

    const visibleWidgets = layout
      .filter((w) => w.visible)
      .sort((a, b) => a.position - b.position);

    return (
      <DragDropContext onDragEnd={this.handleDragEnd}>
        <Droppable droppableId="dashboard-widgets" isDropDisabled={!isCustomizing}>
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                display: "flex",
                flexWrap: "wrap",
                margin: "0 -8px",
              }}
            >
              {visibleWidgets.map((item, index) => {
                const WidgetComponent = widgetComponents[item.widgetId];
                const meta = widgetRegistry[item.widgetId];

                if (!WidgetComponent || !meta) return null;

                const size = meta.defaultSize || { xs: 12 };
                const widthPct = getWidthPercent(size);

                return (
                  <Draggable
                    key={item.widgetId}
                    draggableId={item.widgetId}
                    index={index}
                    isDragDisabled={!isCustomizing}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          width: snapshot.isDragging ? "100%" : widthPct,
                          padding: "0 8px",
                          marginBottom: "16px",
                          boxSizing: "border-box",
                          opacity: snapshot.isDragging ? 0.85 : 1,
                          transition: snapshot.isDragging
                            ? "none"
                            : "opacity 0.2s",
                        }}
                      >
                        <WidgetComponent
                          isCustomizing={isCustomizing}
                          dragHandleProps={provided.dragHandleProps}
                          onHide={() =>
                            this.props.onToggleVisibility(item.widgetId)
                          }
                          detailData={this.props.detailData}
                          perimeter={this.props.perimeter}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    );
  }
}
